"use client"

import { useEffect } from "react"
import type { ReactNode } from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import type { Query } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"

import { useToast } from "@/components/ui/toast"
import {
  AuthenticationError,
  AuthorizationError,
  BadRequestError,
  NotFoundError,
  PaymentRequiredError,
  UnknownError,
  UnprocessableEntityError,
} from "@/services/utils"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false, // Disable retries - can be overridden at query level if needed
    },
  },
})

const AUTHORIZATION_ERROR_MESSAGE =
  "You don't have permission to perform this action. \nIf you believe this is a mistake, please contact your administrator."

const QueryClientProviderWrapper = ({ children }: { children: ReactNode }) => {
  const { showToast } = useToast()

  // Set up error handlers in case of AuthorizationError
  useEffect(() => {
    // Common error toast logic
    const handleErrorToast = (
      error: Error,
      isMutation = false,
      suppressAuthorizationToast = false
    ) => {
      if (error instanceof AuthenticationError) {
        // Intentionally ignore toasts for authentication errors
        // it is handled by redirects
        return
      }
      if (error instanceof BadRequestError || error instanceof NotFoundError) {
        // For queries, the UI should handle these gracefully (render error states)
        // For mutations, show toast feedback since there's no UI to render error state
        if (isMutation) {
          showToast("Something went wrong.", "error")
        }
        return
      }
      if (error instanceof PaymentRequiredError) {
        if (isMutation) {
          showToast(
            "Your subscription is inactive. Please renew to make changes.",
            "error"
          )
        }
        return
      }
      if (error instanceof AuthorizationError) {
        // A query can opt out of the permission toast (e.g. the document page
        // renders its own "You need access" state for a 403, making the toast
        // redundant). Mutations always surface it.
        if (suppressAuthorizationToast) {
          return
        }
        // Show the custom error message from the server
        showToast(error.message || AUTHORIZATION_ERROR_MESSAGE, "error")
      }
      if (error instanceof UnprocessableEntityError) {
        showToast(error.message, "error")
      }
      if (error instanceof UnknownError) {
        showToast("Something went wrong.", "error")
      }
    }

    // Handler for query errors - includes query object to check meta
    // In TanStack Query v5, meta is accessed via query.options.meta
    queryClient.getQueryCache().config.onError = (
      error: Error,
      query: Query<unknown, unknown, unknown>
    ) => {
      // Skip toast if the query has skipToast meta option
      if (query.options.meta?.skipToast) {
        return
      }

      // Suppress error toasts when the query already has cached data.
      // This means the error is from a refetch (polling, invalidation, navigation
      // back to a cached page) rather than the user's initial load of the page.
      // In all these cases, stale data remains visible and a generic "Something
      // went wrong" toast is confusing. Components can still detect refetch errors
      // via query.isRefetchError and render inline error states if needed.
      // Mutation errors are handled separately below and always show toasts.
      // The backend still logs/reports all errors via Datadog.
      if (query.state.data !== undefined) {
        return
      }

      handleErrorToast(
        error,
        false,
        Boolean(query.options.meta?.suppressAuthorizationToast)
      )
    }

    // Handler for mutation errors - no query object available
    queryClient.getMutationCache().config.onError = (error: Error) => {
      handleErrorToast(error, true)
    }
  }, [showToast])

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
    </QueryClientProvider>
  )
}

export { QueryClientProviderWrapper }
